import type { Session } from "@fra.me/terminal-factory";
import { createInstance, SessionState, TerminalEvent } from "@fra.me/terminal-factory";
import * as Sentry from "@sentry/browser";
import { toggleKwInputs, toggleStartButton } from "./ui.js";
import { pollSession } from "./utils.js";

/**
 * Frame Terminal class is not exported from @fra.me/terminal-factory.
 * Derive its type from the createInstance return value — this is safe and stable.
 * See: node_modules/@fra.me/terminal-factory/api.d.ts
 *
 * TODO: Replace with exported Terminal type if the SDK adds one in a future release.
 */
type FrameTerminal = Awaited<ReturnType<typeof createInstance>>;

/**
 * The canonical set of DOM elements and configuration the session functions
 * depend on, passed explicitly to avoid closure coupling and enable isolated
 * testing. Individual functions declare only the fields they read via
 * `Pick<SessionDependencies, ...>` (Interface Segregation) — see
 * `checkUntilClosed` and `startSession` below; `loadFrame`, the widest consumer,
 * takes the full interface. This mirrors the `AccountConfig` ISP note.
 */
export interface SessionDependencies {
  /** The launch/resume button element. */
  startButton: HTMLButtonElement;
  /** The status message element. */
  statusMessage: HTMLElement;
  /** The login form fieldset. */
  loginFieldset: HTMLFieldSetElement;
  /** The KW number input. */
  kwInput: HTMLInputElement;
  /** The KW password input. */
  kwPasswordInput: HTMLInputElement;
  /** The username input. */
  userNameInput: HTMLInputElement;
  /** The Frame terminal instance (null before loadFrame is called). */
  terminal: FrameTerminal | null;
  /** The Frame GraphQL service URL. */
  serviceUrl: string;
}

/**
 * Frame account configuration entry.
 * Only declares fields required by session.ts (Interface Segregation).
 */
export interface AccountConfig {
  readonly terminalConfigId: string;
  readonly applicationId?: string;
}

/**
 * Returns session.state as a comparable string.
 *
 * SDK NOTE: @fra.me/terminal-factory v7 types Session.state as `typeof SessionState`
 * (the whole constant object) rather than as a string value. At runtime the value IS
 * a string like "OPEN" or "CLOSING". This helper applies a cast and documents the
 * SDK type inaccuracy in one place.
 * See api.d.ts line 160: `state: typeof SessionState`.
 * TODO: Remove cast when SDK fixes the type declaration.
 */
export function sessionStateString(session: Session): string {
  return session.state as unknown as string;
}

/**
 * Checks if the terminal session is closing and waits until it is closed.
 * Updates the UI to indicate the closing status and readiness to launch.
 *
 * @param deps - Dependencies object containing UI elements and terminal.
 * @returns A promise that resolves to true if the session is closed.
 */
export async function checkUntilClosed(
  deps: Pick<SessionDependencies, "terminal" | "startButton" | "statusMessage">
): Promise<boolean> {
  const { terminal, startButton, statusMessage } = deps;
  // SDK types getOpenSession() as non-nullable, but at runtime it CAN return null.
  const session = (await (terminal as FrameTerminal).getOpenSession()) as Session | null;

  if (session && session.state) {
    const isClosing = sessionStateString(session) === SessionState.CLOSING;
    if (isClosing) {
      toggleStartButton(startButton, false, "Closing last session...");
      statusMessage.innerText =
        "We're doing some cleanup from the previous session. We'll be ready to launch soon.";

      const sessionGetter = (): Promise<Session | null> =>
        (terminal as FrameTerminal).getOpenSession();

      const isClosingCheck = (result: Session | null): boolean =>
        result !== null && sessionStateString(result) === SessionState.CLOSING;

      await pollSession(sessionGetter, isClosingCheck, 1000);

      return true;
    }
  }
  // Session is either null (closed) or not in CLOSING state (already closed)
  return true;
}

/**
 * Start or resume a Frame session.
 *
 * @param applicationId - ID of the Frame application to start.
 * @param deps - Dependencies object containing UI elements and terminal.
 * @returns The started or resumed session.
 * @throws {Error} If the terminal is not loaded.
 */
export async function startSession(
  applicationId: string,
  deps: Pick<SessionDependencies, "terminal" | "kwInput" | "kwPasswordInput" | "userNameInput">
): Promise<Session> {
  const { terminal, kwInput, kwPasswordInput, userNameInput } = deps;

  Sentry.setContext("Application", { id: applicationId });

  if (!terminal) {
    throw new Error(
      `Terminal not loaded. Could not start session. Application ID: ${applicationId}`
    );
  }

  let session = (await terminal.getOpenSession()) as Session | null;
  if (session) {
    Sentry.addBreadcrumb({
      category: "session",
      message: `Resuming session ${session.id}`,
      level: "info",
    });
    session = await terminal.resume(session.id);
  } else {
    Sentry.addBreadcrumb({
      category: "session",
      message: `Starting session for application ${applicationId}`,
      level: "info",
    });
    const sessionData = `${kwInput.value}_${kwPasswordInput.value}_${userNameInput.value}`;
    session = await terminal.start({ appId: applicationId, userData: sessionData });
    Sentry.addBreadcrumb({
      category: "session",
      message: `Session started ${session.id}`,
      level: "info",
    });
  }
  return session;
}

/**
 * Loads a Frame terminal instance with the given account config and sets up event handlers.
 *
 * @param accountConfig - Frame account configuration.
 * @param token - Frame authentication token.
 * @param deps - Dependencies object containing UI elements and terminal reference.
 * @param setTerminal - Callback to set the terminal instance.
 * @returns void
 * @throws {Error} If the terminal fails to load.
 */
export async function loadFrame(
  accountConfig: AccountConfig,
  token: string,
  deps: SessionDependencies,
  setTerminal: (terminal: FrameTerminal) => void
): Promise<void> {
  const { startButton, statusMessage, loginFieldset, kwInput, kwPasswordInput, userNameInput } =
    deps;

  const sessionOptions = {
    serviceUrl: deps.serviceUrl,
    terminalConfigId: accountConfig.terminalConfigId,
    token,
  };

  try {
    const terminal = await createInstance(sessionOptions);
    setTerminal(terminal);

    terminal.bind(TerminalEvent.SESSION_STARTING, () => {
      statusMessage.innerText = "Connecting...";
      toggleStartButton(startButton, false, "Connecting...");
      loginFieldset.disabled = true;
    });

    terminal.bind(TerminalEvent.SESSION_RESUMING, () => {
      statusMessage.innerText = "Resuming previous session...";
      toggleStartButton(startButton, false, "Resuming...");
      loginFieldset.disabled = true;
    });

    terminal.bind(TerminalEvent.SESSION_STARTED, () => {
      statusMessage.innerText = "Connected.";
      loginFieldset.disabled = true;
      toggleStartButton(startButton, false, "Connected.");
    });

    terminal.bind(TerminalEvent.SESSION_RESUMED, () => {
      statusMessage.innerText = "Connected.";
      loginFieldset.disabled = true;
      toggleStartButton(startButton, false, "Connected.");
    });

    terminal.bind(TerminalEvent.SESSION_DISCONNECTED, async () => {
      statusMessage.innerText =
        "Disconnected. Click Resume to reconnect to your previous session or change location to start a new session.";
      loginFieldset.disabled = false;
      toggleKwInputs(kwInput, kwPasswordInput, userNameInput, false);
      toggleStartButton(startButton, true, "Resume");

      const sessionClosed = await checkUntilClosed({ ...deps, terminal });
      if (sessionClosed) {
        statusMessage.innerText = "Session disconnected. Click Launch to start a new session.";
        toggleStartButton(startButton, true, "Launch");
        loginFieldset.disabled = false;
        toggleKwInputs(kwInput, kwPasswordInput, userNameInput, true);
      }
    });

    terminal.bind(TerminalEvent.SESSION_CLOSED, async () => {
      statusMessage.innerText = "Closing session...";
      toggleStartButton(startButton, false, "Closing...");

      const sessionClosed = await checkUntilClosed({ ...deps, terminal });
      if (sessionClosed) {
        statusMessage.innerText = "Session ended.";
        loginFieldset.disabled = false;
        toggleStartButton(startButton, true, "Launch");
      }
    });

    // check for open session right away
    const session = (await terminal.getOpenSession()) as Session | null;
    if (session && sessionStateString(session) === SessionState.OPEN) {
      loginFieldset.disabled = false;
      toggleKwInputs(kwInput, kwPasswordInput, userNameInput, false);
      statusMessage.innerText =
        "An active session is already running. Click Resume to reconnect or change location to start a new session.";
      toggleStartButton(startButton, true, "Resume");
    } else if (session && sessionStateString(session) === SessionState.CLOSING) {
      const sessionClosed = await checkUntilClosed({ ...deps, terminal });
      if (sessionClosed) {
        statusMessage.innerText = "Click Launch to start a new session.";
        toggleStartButton(startButton, true, "Launch");
        loginFieldset.disabled = false;
        toggleKwInputs(kwInput, kwPasswordInput, userNameInput, true);
      }
    } else if (session === null) {
      loginFieldset.disabled = false;
      toggleKwInputs(kwInput, kwPasswordInput, userNameInput, true);
      toggleStartButton(startButton, true, "Launch");
    }
  } catch (error) {
    console.warn(`Error loading Frame terminal: ${String(error)}`);
    throw error;
  }
}
